/**
 * MapMeasurementsPanel — Listă măsurători (hartă + plan) cu buton "Nouă".
 *
 * Coordonează editoarele MapMeasureEditor (Google Maps) și
 * PlanCalibrationEditor (plan scanat calibrat).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Map as MapIcon, Plus, Trash2, Ruler, MapPin, Loader2, Image as ImageIcon, FileText,
} from "lucide-react";
import MapMeasureEditor from "./MapMeasureEditor";
import PlanCalibrationEditor from "./PlanCalibrationEditor";
import ExportToDevizDialog from "./ExportToDevizDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MapMeasurementsPanel({ companyId, onDevizCreated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null); // { mode: 'map'|'plan', measurement: null|obj }
  const [exportTarget, setExportTarget] = useState(null); // measurement doc

  const params = useMemo(() => ({ company_id: companyId }), [companyId]);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/sketches/measurements`, { params });
      setItems(data || []);
    } catch (e) {
      console.error(e);
      toast.error("Nu am putut încărca măsurătorile");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openEdit = async (id, mode) => {
    try {
      const { data } = await axios.get(`${API}/sketches/measurements/${id}`, { params });
      setEditing({ mode: data.mode || mode, measurement: data });
    } catch (e) {
      console.error(e);
      toast.error("Nu am putut deschide măsurătoarea");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ștergi această măsurătoare? Acțiunea nu poate fi anulată.")) return;
    try {
      await axios.delete(`${API}/sketches/measurements/${id}`, { params });
      toast.success("Măsurătoare ștearsă");
      fetchList();
    } catch (e) {
      toast.error("Nu am putut șterge");
    }
  };

  // -- Editing view --
  if (editing) {
    const back = () => { setEditing(null); fetchList(); };
    if (editing.mode === "plan") {
      return (
        <PlanCalibrationEditor
          companyId={companyId}
          measurement={editing.measurement}
          onBack={back}
          onSaved={(saved) => setEditing({ mode: "plan", measurement: saved })}
          onExport={(saved) => { setExportTarget(saved); setEditing({ mode: "plan", measurement: saved }); }}
        />
      );
    }
    return (
      <>
        <MapMeasureEditor
          companyId={companyId}
          measurement={editing.measurement}
          onBack={back}
          onSaved={(saved) => setEditing({ mode: "map", measurement: saved })}
          onExport={(saved) => { setExportTarget(saved); setEditing({ mode: "map", measurement: saved }); }}
        />
        <ExportToDevizDialog
          companyId={companyId}
          open={!!exportTarget}
          onOpenChange={(v) => { if (!v) setExportTarget(null); }}
          measurement={exportTarget}
          onDone={(res) => { setExportTarget(null); onDevizCreated && onDevizCreated(res); }}
        />
      </>
    );
  }

  // -- List view --
  return (
    <>
      <Card className="border-0 shadow-md" data-testid="map-measurements-panel">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MapIcon className="w-5 h-5 text-teal-600" />
                Măsurători Schițe
                <Badge className="bg-teal-100 text-teal-700 border-0">Hartă + Plan</Badge>
              </CardTitle>
              <CardDescription>
                Măsoară acoperișuri, terenuri și clădiri pe hartă satelit gratuită (Esri) sau
                pe planuri scanate calibrate manual. Exportă direct în DevizePRO cu
                template-uri de hidroizolații.
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowNew(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="map-measurement-new-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Măsurătoare nouă
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Se încarcă…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-14 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
              <MapIcon className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm mb-3">
                Nu ai nicio măsurătoare. Începe cu <b>Măsurătoare nouă</b> — caută adresa,
                delimitează suprafețele, apoi exportă în DevizePRO.
              </p>
              <Button
                onClick={() => setShowNew(true)}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Plus className="w-4 h-4 mr-1" /> Măsurătoare nouă
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((m) => (
                <div
                  key={m.id}
                  className="group border border-slate-200 rounded-lg p-3 bg-white hover:border-teal-300 hover:shadow-md transition cursor-pointer"
                  onClick={() => openEdit(m.id, m.mode)}
                  data-testid={`measurement-card-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {m.mode === "plan" ? (
                          <ImageIcon className="w-3.5 h-3.5 text-violet-600" />
                        ) : (
                          <MapIcon className="w-3.5 h-3.5 text-teal-600" />
                        )}
                        <h3 className="font-semibold text-sm text-slate-800 truncate">{m.name}</h3>
                      </div>
                      {m.address && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate flex items-center gap-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" /> {m.address}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100"
                      onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                    <Badge className="bg-teal-50 text-teal-700 border-0 text-[10px]">
                      <Ruler className="w-3 h-3 mr-1" /> {(m.total_area_m2 || 0).toFixed(2)} m²
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      Perim: {(m.total_perimeter_m || 0).toFixed(2)} m
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {m.mode === "plan" ? "Plan scanat" : "Hartă"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-slate-100">
                    <span className="text-[10px] text-slate-400">
                      {m.updated_at ? new Date(m.updated_at).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-teal-700 hover:bg-teal-50"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const { data } = await axios.get(`${API}/sketches/measurements/${m.id}`, { params });
                          setExportTarget(data);
                        } catch (err) { toast.error("Nu am putut deschide"); }
                      }}
                      data-testid={`measurement-export-${m.id}`}
                    >
                      <FileText className="w-3.5 h-3.5 mr-1" /> Deviz
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Choose mode dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Măsurătoare nouă</DialogTitle>
            <DialogDescription>Cum vrei să faci măsurătoarea?</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              onClick={() => { setShowNew(false); setEditing({ mode: "map", measurement: null }); }}
              className="rounded-lg border border-slate-200 p-4 text-left hover:border-teal-500 hover:bg-teal-50/50 transition"
              data-testid="new-measurement-map-btn"
            >
              <MapIcon className="w-6 h-6 text-teal-600 mb-2" />
              <div className="font-semibold text-slate-800 text-sm">Pe hartă satelit</div>
              <p className="text-xs text-slate-600 mt-1">
                Caută adresa, delimitează clădirea pe imagini satelit (Esri). Import KML/GeoJSON acceptat.
              </p>
            </button>
            <button
              type="button"
              onClick={() => { setShowNew(false); setEditing({ mode: "plan", measurement: null }); }}
              className="rounded-lg border border-slate-200 p-4 text-left hover:border-violet-500 hover:bg-violet-50/50 transition"
              data-testid="new-measurement-plan-btn"
            >
              <ImageIcon className="w-6 h-6 text-violet-600 mb-2" />
              <div className="font-semibold text-slate-800 text-sm">Pe plan scanat</div>
              <p className="text-xs text-slate-600 mt-1">
                Încarcă un plan CAD/scan și calibrează scara cu o cotă cunoscută pentru precizie centimetrică.
              </p>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <ExportToDevizDialog
        companyId={companyId}
        open={!!exportTarget && !editing}
        onOpenChange={(v) => { if (!v) setExportTarget(null); }}
        measurement={exportTarget}
        onDone={(res) => { setExportTarget(null); onDevizCreated && onDevizCreated(res); }}
      />
    </>
  );
}
